import {
  ArticleCardElementsOrientation,
  ArticleCardStyleProps,
  ArticleCardVariant,
} from "@config/articleCard";

const HORIZONTAL_PACKAGE_CARD_STYLE: ArticleCardStyleProps = {
  cardWrapper:
    "flex flex-col items-center justify-center basis-[17%] break-words overflow-auto",
  headerWrapper: "block w-full",
  contentWrapper: "w-full",
  dateStyle: "text-xxs text-text-muted",
  sectionNameWrapper: "flex flex-col items-start gap-2",
  sectionNameStyle: "text-[12px] lg:text-[12px] font-semibold",
  dateTitleWrapper: "flex flex-col-reverse gap-sm mt-xs",
  titleStyle:
    "lg:text-sm font-medium font-secondary text-text-body leading-[19.2px]",
  titleHoverStyle: "hover:text-brand-primary",
};

const VERTICAL_PACKAGE_CARD_STYLE_FIRST: ArticleCardStyleProps = {
  cardWrapper: "flex flex-col lg:flex-row justify-center w-full gap-md",
  hero: "mb-0",
  headerWrapper: "basis-3/5 block w-full",
  contentWrapper: "basis-2/5 w-full mb-0",
  dateStyle: "text-text-muted font-semimedium leading-[21px]",
  dateTitleWrapper: "flex flex-col-reverse justify-between flex-grow gap-sm",
  titleStyle:
    "lg:text-[20px] font-medium font-secondary text-text-body leading-[30px]",
  titleHoverStyle: "hover:text-brand-primary",
  sectionNameStyle: "text-[12px] lg:text-xxs font-semibold",
};

const VERTICAL_PACKAGE_CARD_STYLE_REST: ArticleCardStyleProps = {
  cardWrapper: "flex justify-center w-full gap-xs",
  hero: "mb-xs",
  headerWrapper: "basis-3/10 md:basis-2/5 lg:basis-3/10 block w-full",
  contentWrapper: "basis-7/10 md:basis-3/5 lg:basis-7/10 w-full mb-0 gap-0",
  dateStyle: "text-text-muted font-semimedium leading-[21px]",
  dateTitleWrapper: "flex flex-col-reverse gap-xs",
  titleStyle:
    "lg:text-sm font-medium font-secondary text-text-body leading-[19.2px]",
  titleHoverStyle: "hover:text-brand-primary",
  sectionNameStyle: "text-[12px] lg:text-[12px] font-semibold",
  sectionNameWrapper: "justify-between",
};

const ARTICLE_CARD_HORIZONTAL_SUMMARY_DEFAULT_STYLE: ArticleCardStyleProps = {
  cardWrapper: "flex flex-row gap-4",
  contentWrapper: "gap-2",
  imageStyle: "w-[138px] md:w-[226px] xl:w-[284px]",
  hero: "mb-0",
  description:
    "font-primary text-xs leading-[150%] font-regular mb-sm hidden md:block",
  dateStyle: "md:py-0 text-xxs lg:text-sm",
  sectionNameItem: "lg:justify-start w-auto",
  titleStyle: "font-semimedium",
};

// --- The Style Resolver Function ---

export default function getArticleCardStyles({
  orientation,
  variant,
}: {
  orientation: ArticleCardElementsOrientation;
  variant: ArticleCardVariant;
}): ArticleCardStyleProps {
  switch (orientation) {
    case "vertical":
      if (variant === "featured") {
        return {
          ...HORIZONTAL_PACKAGE_CARD_STYLE,
          cardWrapper:
            "flex flex-row gap-xs xl:gap-0 xl:flex-col xl:items-center justify-center xl:basis-1/4",
          headerWrapper: "basis-3/10 md:basis-2/5 lg:basis-3/10 block w-full",
          contentWrapper:
            "basis-7/10 md:basis-3/5 lg:basis-7/10 w-full mb-0 gap-xs text-xxs",
          dateTitleWrapper: "flex flex-col-reverse gap-sm",
          sectionNameWrapper: "flex justify-between",
        };
      }

      // used in myBH starting layout
      if (variant === "xxl") {
        return {
          ...HORIZONTAL_PACKAGE_CARD_STYLE,
          cardWrapper: "flex flex-col items-center justify-center",
          headerWrapper: "basis-3/10 md:basis-2/5 lg:basis-3/10 block w-full",
          contentWrapper:
            "basis-7/10 md:basis-3/5 lg:basis-7/10 w-full mb-0 gap-xs text-xxs",
          dateTitleWrapper: "flex flex-col-reverse gap-2.5",
          sectionNameWrapper: "flex justify-between",
        };
      }
      return HORIZONTAL_PACKAGE_CARD_STYLE;

    case "vertical-reversed":
      if (variant === "small") {
        return { cardWrapper: "flex flex-col gap-0", imageStyle: "w-full" };
      }
      return {};

    case "vertical-summary":
      if (variant === "small") {
        return {
          cardWrapper: "flex flex-col",
          contentWrapper:
            "flex flex-col-reverse md:flex-col mt-2 md:mt-0 md:mb-1 flex-grow",
          imageStyle: "w-full",
          hero: "w-full",
        };
      }
      if (variant === "medium") {
        return {
          cardWrapper: "flex flex-col gap-2",
          contentWrapper: "flex flex-col gap-2",
          dateTitleWrapper: "text-right",
          titleParaWrapper: "text-left",
          titleStyle: "font-semibold",
          description:
            "font-primary text-neutral-700 text-xs font-regular leading-[140%]",
          imageStyle: "w-full",
          hero: "w-full",
        };
      }

      // used in myBH starting layout for first article
      if (variant === "xxl") {
        return {
          ...HORIZONTAL_PACKAGE_CARD_STYLE,
          cardWrapper: "flex flex-col items-center justify-center",
          headerWrapper: "basis-3/10 md:basis-2/5 lg:basis-3/10 block w-full",
          contentWrapper:
            "basis-7/10 md:basis-3/5 lg:basis-7/10 w-full mb-0 gap-xs text-xxs",
          dateTitleWrapper: "flex flex-col-reverse gap-2.5",
          sectionNameWrapper: "flex justify-between",
          titleParaWrapper: "flex flex-col gap-2.5",
          description: "lg:text-sm leading-[150%] text-neutral-700",
        };
      }
      return {};

    case "horizontal":
      switch (variant) {
        case "small":
        case "xl":
        case "xxl":
        case "featured":
          return VERTICAL_PACKAGE_CARD_STYLE_FIRST;
        case "medium":
        case "2md":
          return {
            ...VERTICAL_PACKAGE_CARD_STYLE_REST,
            contentWrapper:
              "basis-7/10 md:basis-3/5 lg:basis-7/10 w-full mb-0 gap-xs",
          };
        case "large":
          return VERTICAL_PACKAGE_CARD_STYLE_REST;
        default:
          return {};
      }

    case "horizontal-summary":
      if (variant === "small") {
        return {
          ...VERTICAL_PACKAGE_CARD_STYLE_REST,
          headerWrapper:
            "max-w-[220px] basis-3/10 md:basis-2/5 lg:basis-3/10 block w-full",
          contentWrapper:
            "basis-7/10 md:basis-3/5 lg:basis-7/10 w-full mb-0 gap-0",
        };
      }
      if (variant === "medium" || variant === "2md") {
        return ARTICLE_CARD_HORIZONTAL_SUMMARY_DEFAULT_STYLE;
      }
      if (variant === "large") {
        return {
          ...ARTICLE_CARD_HORIZONTAL_SUMMARY_DEFAULT_STYLE,
          sectionNameWrapper:
            "flex-col items-start md:flex-row md:items-center gap-1 md:gap-3",
        };
      }
      return {};

    default:
      return {};
  }
}
